import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  Input,
  OnDestroy,
  OnChanges,
  NgModule,
  SimpleChanges,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Subject, from } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import {
  DxButtonModule,
  DxScrollViewModule,
  DxRadioGroupModule,
  DxPopupModule,
  DxSwitchModule,
} from 'devextreme-angular';
import { SessionService } from 'src/app/shared/services/session.service';

import { Cart } from 'src/app/shared/models/cart';
import { CartService } from 'src/app/shared/services/cart/cart.service';
import { Listing } from 'src/app/shared/models/listing';
import { MsTooltipModule } from 'src/app/shared/components/ms-tooltip/ms-tooltip.component';
import { AddToCartModule } from 'src/app/shared/components/add-to-cart/add-to-cart.component';
import { PipeModule } from 'src/app/pipe.module';
import { ListingWishlistToggleModule } from '../listing-wishlist-toggle/listing-wishlist-toggle.component';
import { environment } from 'src/environments/environment';
import { UserService } from '../../services/user/user.service';
import { confirm } from 'devextreme/ui/dialog';
import { SellerCompanyLogoModule } from 'src/app/shared/components/seller-company-logo/seller-company-logo.module';
import { FeatureFlagsService } from '../../services/featureFlags.service';
import {
  FacilityDocumentStatus,
  FacilityDocumentsService,
} from '../../services/facility-documents.service';
import { ScreenService } from '../../services/screen.service';

@Component({
  selector: 'app-cart-side',
  templateUrl: './cart-side.component.html',
  styleUrls: ['./cart-side.component.scss'],
})
export class CartSideComponent implements OnInit, OnDestroy, OnChanges {
  @Output() visibleOut: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onCartModeC2Change: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Input() mode;
  @Input() expeditedShipping: boolean;
  @Input() initialCartData;
  // MDG-101: qty-tier tip — provided by the checkout page, rendered under the matching item
  @Input() qtyTip;
  @Output() qtyTipSwitch: EventEmitter<void> = new EventEmitter<void>();

  public cartModeC2 = false;

  cartData: Cart = new Cart();
  currentListing: Listing;

  optionsByListingId = {};
  optionsById = {};
  selectedShippingByListing = {};
  savingShippingByListing = {};
  shippingExpeditedCartMode = false;
  facilityDocumentStatus: FacilityDocumentStatus = {
    expiredLicense: false,
    expiredMessage: '',
    state_license_missing: false,
    state_license_expired: false,
    dea_license_missing: false,
    dea_license_expired: false,
  };
  showExpiredLicense = false;
  showControlledSubstanceSuffix = false;
  addVisible = false;
  loading = false;
  itemsInCart = 0;
  itemsInCartC2 = 0;
  showC2Switch = true;
  hydraEnabled = environment.hydraEnabled;
  paymentOption: string;
  private onDestroy$: Subject<void> = new Subject<void>();
  deliveryComment = environment.hydraEnabled
    ? environment.supplierConfig.misc.deliveryComment
    : '';
  user: any;

  isSmall = false;
  bucketDictionary = {};

  constructor(
    private cartService: CartService,
    private userService: UserService,
    private featureFlagsService: FeatureFlagsService,
    private sessionService: SessionService,
    private facilityDocumentsService: FacilityDocumentsService,
    private router: Router,
    private screen: ScreenService,
  ) {
    this.monitorScreen();
  }

  async ngOnInit() {
    await this.setPaymentOption();
    this.setBucketDictionary();
    this.facilityDocumentCheck();
    this.cartRefreshingCheck();
    this.cartModeC2 = this.cartService.getCartModeC2();
    this.shippingExpeditedCartMode = this.featureFlagsService.get(
      'shipping_expedited_checkbox_mode',
    );

    if (this.initialCartData) {
      this.cartData = this.initialCartData;
      this.afterCartLoaded();
    } else {
      this.getCart();
    }
    this.watchPrimaryFacilityUpdatedTrigger();

  }

  watchPrimaryFacilityUpdatedTrigger() {
    this.sessionService.primaryFacilityUpdatedTrigger
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(async () => {
          this.user = await this.userService.getMe(true).toPromise();
          this.facilityDocumentCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.expeditedShipping || !this.cartData) return;
    if (changes.expeditedShipping.firstChange) return;

    const expedited = !!changes.expeditedShipping.currentValue;

    if (this.cartData && !!this.cartData.expedited === expedited) return;

    const cartToSend = {
      ...this.cartData,
      expedited,
    };

    from(this.cartService.storeCart(cartToSend, this.cartModeC2))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        () => this.getCart(),
        () => this.getCart(),
      );
  }

  private setBucketDictionary() {
    try {
      this.bucketDictionary =
        this.featureFlagsService.get('bucket_names_map')[
          environment.supplierConfig.clientName
        ];
    } catch (e) {
      this.bucketDictionary = {};
    }
  }

  getBucket(priceType: string) {
    const value = this.bucketDictionary?.[priceType];
    return value ?? null;
  }

  async facilityDocumentCheck() {
    this.facilityDocumentStatus =
      await this.facilityDocumentsService.getStatus(
        this.user?.data?.primary_facility,
      );
    const cartHasControlledSubstance = this.cartData.items?.some(item =>
      ['2', '3', '4', '5'].includes(item.dea),
    );

    this.showExpiredLicense = this.facilityDocumentsService.isBlocking(
      this.facilityDocumentStatus,
      cartHasControlledSubstance,
    );
    this.showControlledSubstanceSuffix =
      cartHasControlledSubstance &&
      this.facilityDocumentStatus.dea_license_expired;
  }

  async setPaymentOption() {
    this.user = await this.userService.getMe().toPromise();
    this.paymentOption = this.user.data.company.payment_option;
    this.facilityDocumentCheck();
  }

  cartRefreshingCheck() {
    this.cartService.cartRefreshing
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(result => this.onCartRefreshing(result));
  }

  onCartRefreshing(value) {
    this.loading = value;
    if (!this.loading) {
      this.cartModeC2 = this.cartService.getCartModeC2();
      this.getCart();
      this.facilityDocumentCheck();
    }
  }

  switchCartModeC2(e) {
    this.cartModeC2 = e.value;
    this.cartService.setCartModeC2(this.cartModeC2);
    this.getCart();
    this.onCartModeC2Change.emit(this.cartModeC2);
  }

  getCart() {
    this.getCartData(true);
    this.getCartData(false);
  }

  getCartData(isC2) {
    this.cartService
      .get(isC2)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(result => {
        if (isC2) {
          this.itemsInCartC2 = result?.items?.length;
        } else {
          this.itemsInCart = result?.items?.length;
        }
        if (result !== null) {
          this.showC2Switch = this.itemsInCart > 0 && this.itemsInCartC2 > 0;
          if (!this.itemsInCartC2) {
            this.cartModeC2 = false;
          }
          if (!this.itemsInCart && this.itemsInCartC2) {
            this.cartModeC2 = true;
          }
          this.cartService.setCartModeC2(this.cartModeC2);
          if (this.cartModeC2 == isC2) {
            this.cartData = result;
            this.expeditedShipping = !!result.expedited;
            this.afterCartLoaded();
          }
        }
      });
  }

  afterCartLoaded() {
    this.cartData.items.forEach(item => {
      item.tota_quantity = 0;
      item.sub_total = 0;
      item.pricing.prices.forEach(price => {
        item.tota_quantity += price.quantity;
        item.sub_total += price.quantity * price.price;
      });
    });

    this.seedShippingMapsFromCart(this.cartData);
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  checkout() {
    this.visibleOut.emit(false);
    this.cartService.refresh().then(() => {
      this.router.navigate(['/cart']);
    });
  }

  closeCart() {
    this.visibleOut.emit(false);
  }

  seedShippingMapsFromCart(cart) {
    this.optionsByListingId = {};
    this.optionsById = {};
    this.selectedShippingByListing = {};
    this.savingShippingByListing = {};

    const toNum = v => (v === null || v === undefined ? null : Number(v));

    cart.items.forEach(item => {
      const enabledOpts = item.options.filter(
        o => Number(o.enabled ?? 1) === 1,
      );

      enabledOpts.forEach(o => {
        this.optionsById[o.id] = o;
      });

      const nonPrimary = enabledOpts
        .filter(o => Number(o.primary) !== 1)
        .map(o => ({ ...o, id: toNum(o.id) }));

      this.optionsByListingId[item.id] = nonPrimary;

      const existing = toNum(item.shipping_option_id);
      // Don't pre-select a shipping option when expedited is active — price is TBD
      this.selectedShippingByListing[item.id] = this.expeditedShipping ? null : existing;
    });
  }

  onListingShippingChanged(listing, e) {
    const nextId = Number(e?.value);
    if (Number.isNaN(nextId)) return;

    const prevId = this.selectedShippingByListing[listing.id];
    if (prevId === nextId) return;

    this.selectedShippingByListing[listing.id] = nextId;
    listing.shipping_option_id = nextId;

    const item = this.cartData.items.find(it => it.id == listing.id);
    item.shipping_option_id = nextId;
    this.persistListingShipping(listing);
  }

  persistListingShipping(listing) {
    this.savingShippingByListing[listing.id] = true;

    from(this.cartService.storeCart(this.cartData, this.cartModeC2))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        () => {},
        () => {},
      )
      .add(() => {
        this.savingShippingByListing[listing.id] = false;
      });
  }

  removeItem(lineId, isC2, name) {
    let result = confirm(
      `Are you sure you want to remove <b>${name}</b> from cart?`,
      'Confirm Removal',
    );
    result.then(dialogResult => {
      if (dialogResult) {
        this.cartService.delete(lineId, isC2);
      }
    });
  }

  editItem(listing) {
    this.currentListing = listing;
    this.addVisible = true;
  }

  onProductDetailsClick(listing) {
    if (this.isSmall) {
      this.router.navigate([`/detailed/${listing.id}`]);
    } else {
      this.editItem(listing);
    }
  }

  monitorScreen() {
    this.setScreenStates();
    this.screen.changed.subscribe(() => this.setScreenStates());
  }

  setScreenStates() {
    this.isSmall =
      this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'];
  }

  addOutput() {
    this.addVisible = false;
  }

  upperCasePriceType(type) {
    if (type === '340b') {
      return '340B';
    } else {
      return type.toUpperCase();
    }
  }
}

@NgModule({
  imports: [
    CommonModule,
    DxButtonModule,
    DxScrollViewModule,
    MsTooltipModule,
    DxRadioGroupModule,
    DxPopupModule,
    AddToCartModule,
    RouterModule,
    PipeModule,
    DxSwitchModule,
    ListingWishlistToggleModule,
    SellerCompanyLogoModule
  ],
  declarations: [CartSideComponent],
  exports: [CartSideComponent],
})
export class CartSideModule {}
